import { Drawer } from 'antd';
import { useContext } from 'react';

import { Spinner } from 'app/components/commons/Spinner/Spinner';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { useBooking } from 'app/hooks/data/core/extranet/bookings/useBooking';
import { useMoveZendesk } from 'app/hooks/useZendeskWidget/useMoveZendesk';
import { dateAndHour } from 'app/utils/dates';
import { capitalizeFirstLetter } from 'app/utils/string';

import { BookingDetailSection } from '../../../common/BookingDetailSection/BookingDetailSection';
import { Help } from '../../../common/Help/Help';

import styles from './BookingDetails.module.scss';
import { BookingHeader } from './_components/BookingHeader/BookingHeader';
import { BookingInfo } from './_components/BookingInfo/BookingInfo';
import { PaymentDetails } from './_components/PaymentDetails/PaymentDetails';
import { ModernVirtualCard } from './_components/VirtualCard/VirtualCard';

type BookingDetailsProps = {
  bookingCode?: string;
  onClose: () => void;
};

export const BookingDetails = ({
  bookingCode,
  onClose,
}: BookingDetailsProps) => {
  const { hotelId } = useContext(HotelContext);

  const { isTaskCenterOpen } = useContext(TaskCenterContext);

  const { data: bookingData, isLoading: bookingIsLoading } = useBooking(
    hotelId,
    bookingCode
  );

  useMoveZendesk({
    moveZendesk: true,
    drawerOpen: (!!bookingCode && !!hotelId) || isTaskCenterOpen,
  });

  return (
    <Drawer
      title={
        !!bookingData && !bookingIsLoading ? (
          <div className={styles.detailHeader}>
            <div className={styles.customerInfo}>
              {[
                bookingData.booking.customerData.gender
                  ? `${capitalizeFirstLetter(
                      bookingData.booking.customerData.gender
                    )}.`
                  : '',
                bookingData.booking.customerData.firstName,
                bookingData.booking.customerData.lastName,
              ].join(' ')}
            </div>
            <div className={styles.bookingInfo}>
              Booking date :{' '}
              {dateAndHour(bookingData.booking.payments[0].createdAt)}
            </div>
          </div>
        ) : (
          'Booking Details'
        )
      }
      className={styles.bookingDetails}
      open={!!bookingCode}
      onClose={onClose}
      width={510}
      bodyStyle={{ padding: 0 }}
    >
      {!!bookingData && !bookingIsLoading ? (
        <div key={bookingCode} className={styles.detailsContainer}>
          <BookingHeader
            isIssuingBooking={!!bookingData.booking.issuingCardId}
          />
          <div className={styles.detailContent}>
            <BookingDetailSection title="Booking" name="booking">
              <BookingInfo bookingDetails={bookingData.booking} />
            </BookingDetailSection>
            <BookingDetailSection
              title="Payment Details"
              name="payment-details"
              hasPadding={false}
            >
              <PaymentDetails bookingDetails={bookingData.booking} />
            </BookingDetailSection>
            {bookingData.booking.issuingCardId && (
              <>
                <BookingDetailSection title="Virtual Card" name="virtual-card">
                  <ModernVirtualCard bookingDetails={bookingData.booking} />
                </BookingDetailSection>{' '}
                <BookingDetailSection
                  title="Help"
                  name="help"
                  hasPadding={false}
                >
                  <Help />
                </BookingDetailSection>
              </>
            )}
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </Drawer>
  );
};
